import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  externalUseRows,
  planLabel,
  type LabelLayout,
  type LabelPage,
} from '@/features/dispensing-labels/layout';
import { leafletQrOf } from '@/features/dispensing-labels/printing';
import type { DispensingLabel } from '@/features/dispensing-labels/types';
import { withoutDoctorTitle } from '@/features/dispensing-labels/doctor-name';

/** The pages (one, or a label plus continuation labels) of one medicine label. */
export function useLabelPages(
  label: DispensingLabel,
  layout: LabelLayout,
  marked: boolean,
  slack = 0,
): LabelPage[] {
  const { t } = useTranslation();
  const withQr = leafletQrOf(label) !== null;
  const patientLine = `${label.patient} · ${t('dispensingLabels.rxLine', {
    number: label.rxNumber,
    date: label.date,
  })}`;
  // RL-1644: "Dr Dr Farah Aziz": the title is added only when missing.
  const doctorLine = t('dispensingLabels.doctorLine', {
    name: withoutDoctorTitle(label.doctor),
  });
  const batchLine = label.batch
    ? t('dispensingLabels.batchLine', { batch: label.batch })
    : null;
  const extra = label.externalUse ? externalUseRows(marked).length : 0;
  return useMemo(
    () =>
      planLabel(label, layout, {
        withQr,
        marked,
        doctorLine,
        batchLine,
        patientLine,
        extraExternalUse: extra,
        slack,
      }),
    [
      label,
      layout,
      withQr,
      marked,
      doctorLine,
      batchLine,
      patientLine,
      extra,
      slack,
    ],
  );
}
